'use client';

import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Eye, Sparkles, Star, Zap } from 'lucide-react';

/**
 * Unique View Celebration Component
 * Shows a cinematic celebration when a user earns a unique view
 */
export function UniqueViewCelebration({ show, onComplete }) {
  const [phase, setPhase] = useState(0);

  useEffect(() => {
    if (!show) {
      setPhase(0);
      return;
    }

    // Phase 1: Initial burst (0-500ms)
    const timer1 = setTimeout(() => setPhase(1), 100);
    // Phase 2: Text reveal (500ms-1500ms)
    const timer2 = setTimeout(() => setPhase(2), 600);
    // Phase 3: Points animation (1500ms-2500ms)
    const timer3 = setTimeout(() => setPhase(3), 1600);
    // Phase 4: Fade out (2500ms-3500ms)
    const timer4 = setTimeout(() => {
      setPhase(4);
      onComplete?.();
    }, 3500);

    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
      clearTimeout(timer3);
      clearTimeout(timer4);
    };
  }, [show, onComplete]);

  if (!show && phase === 0) return null;

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed inset-0 z-[100] pointer-events-none flex items-center justify-center"
        >
          {/* Background overlay */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: phase >= 1 ? 0.7 : 0 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-gradient-to-b from-emerald-950/80 via-black/90 to-emerald-950/80"
          />

          {/* Particle burst effect */}
          {phase >= 1 && (
            <div className="absolute inset-0 overflow-hidden">
              {[...Array(20)].map((_, i) => (
                <motion.div
                  key={i}
                  initial={{
                    x: '50vw',
                    y: '50vh',
                    scale: 0,
                    opacity: 1,
                  }}
                  animate={{
                    x: `${Math.random() * 100}vw`,
                    y: `${Math.random() * 100}vh`,
                    scale: [0, 1.5, 0],
                    opacity: [1, 1, 0],
                  }}
                  transition={{
                    duration: 2,
                    delay: i * 0.05,
                    ease: 'easeOut',
                  }}
                  className="absolute"
                >
                  {i % 3 === 0 ? (
                    <Sparkles className="h-6 w-6 text-emerald-400" />
                  ) : i % 3 === 1 ? (
                    <Star className="h-4 w-4 text-yellow-400" />
                  ) : (
                    <div className="h-3 w-3 rounded-full bg-emerald-500" />
                  )}
                </motion.div>
              ))}
            </div>
          )}

          {/* Main content */}
          <div className="relative z-10 flex flex-col items-center gap-6">
            {/* Eye icon with glow */}
            <motion.div
              initial={{ scale: 0, rotate: -180 }}
              animate={{
                scale: phase >= 1 ? [0, 1.5, 1] : 0,
                rotate: phase >= 1 ? 0 : -180,
              }}
              transition={{
                duration: 0.6,
                type: 'spring',
                stiffness: 200,
              }}
              className="relative"
            >
              <motion.div
                animate={{
                  boxShadow:
                    phase >= 1
                      ? [
                          '0 0 20px rgba(16, 185, 129, 0.5)',
                          '0 0 60px rgba(16, 185, 129, 0.8)',
                          '0 0 40px rgba(16, 185, 129, 0.6)',
                        ]
                      : '0 0 0px rgba(16, 185, 129, 0)',
                }}
                transition={{ duration: 1, repeat: 2 }}
                className="p-6 rounded-full bg-gradient-to-br from-emerald-500 to-emerald-700"
              >
                <Eye className="h-16 w-16 text-white" />
              </motion.div>

              {/* Pulsing ring */}
              <motion.div
                initial={{ scale: 1, opacity: 0.8 }}
                animate={{
                  scale: [1, 2, 2.5],
                  opacity: [0.8, 0.4, 0],
                }}
                transition={{
                  duration: 1.5,
                  repeat: 2,
                  repeatDelay: 0.2,
                }}
                className="absolute inset-0 rounded-full border-4 border-emerald-400"
              />
            </motion.div>

            {/* Text */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{
                opacity: phase >= 2 ? 1 : 0,
                y: phase >= 2 ? 0 : 20,
              }}
              transition={{ duration: 0.4 }}
              className="text-center"
            >
              <motion.h2
                className="text-4xl font-bold text-white mb-2"
                animate={{
                  textShadow:
                    phase >= 2
                      ? [
                          '0 0 10px rgba(16, 185, 129, 0.5)',
                          '0 0 30px rgba(16, 185, 129, 0.8)',
                          '0 0 20px rgba(16, 185, 129, 0.6)',
                        ]
                      : '0 0 0px rgba(16, 185, 129, 0)',
                }}
                transition={{ duration: 0.5, repeat: 2 }}
              >
                UNIQUE VIEW EARNED!
              </motion.h2>
              <p className="text-emerald-300 text-lg">
                First time viewing this content
              </p>
            </motion.div>

            {/* Points */}
            <motion.div
              initial={{ opacity: 0, scale: 0 }}
              animate={{
                opacity: phase >= 3 ? 1 : 0,
                scale: phase >= 3 ? [0, 1.2, 1] : 0,
              }}
              transition={{ duration: 0.5, type: 'spring' }}
              className="flex items-center gap-3 px-6 py-3 rounded-full bg-gradient-to-r from-emerald-600/80 to-yellow-600/80 backdrop-blur-sm border border-emerald-400/50"
            >
              <Zap className="h-6 w-6 text-yellow-300" />
              <span className="text-2xl font-bold text-white">
                +10 View Power
              </span>
              <Zap className="h-6 w-6 text-yellow-300" />
            </motion.div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
